<template>
  <span
    class="relative inline-flex w-16 h-6 items-center justify-center rounded-sm select-none font-mono text-xs font-semibold tracking-wide uppercase"
    :title="title"
    :aria-label="title"
    data-field="role"
  >
    <!-- Left half: main weapon #1's school color. Hard edge down the middle. -->
    <span
      class="absolute top-0 bottom-0 left-0 w-1/2 pointer-events-none rounded-l-sm"
      :class="leftBgClass"
    />
    <!-- Right half: main weapon #2's school color (or same as left when only one weapon is equipped). -->
    <span
      class="absolute top-0 bottom-0 right-0 w-1/2 pointer-events-none rounded-r-sm"
      :class="rightBgClass"
    />
    <!--
      Role label sits on top of the two color halves. Text color is dynamic so
      the badge stays legible in every state: role-tinted halves are mid-tone
      and read fine with dark text (`text-text-inverse`), but the neutral
      `bg-bg-surface` fallback (no weapons equipped) is dark enough that dark
      text disappears — flip to `text-text-primary` in that branch only.
    -->
    <span class="relative" :class="labelTextClass">{{ label }}</span>
    <!-- Sentinel: ensures bg-role-tank bg-role-dps bg-role-heal appear as literal substrings in the template body for the Tailwind v4 JIT scanner (the same classes are emitted via leftBgClass / rightBgClass above). Kept as a defense-in-depth — the canonical safelist lives in `src/styles/tailwind.css` via `@source inline(...)`. -->
  </span>
</template>

<script lang="ts">
import { WeaponRoleColor } from '@/types/weapon'
import { CardRole } from '@/features/gvg-planner/lib/roleLabels'

export interface RoleBadgeProps {
  /** Role label rendered on top of the chip (HEAL / TANK / DPS / HYBRID / NAMELESS). */
  readonly role: CardRole
  /** Role-tinted color of main weapon #1 — drives the left half. `null` → neutral. */
  readonly leftPath: WeaponRoleColor | null
  /** Role-tinted color of main weapon #2 — drives the right half. Falls back to `leftPath`. */
  readonly rightPath: WeaponRoleColor | null
}
</script>

<script setup lang="ts">
import { computed } from 'vue'

import { defineLocaleCatalog } from '@/shared/i18n'
import { useLocaleMessages } from '@/shared/composables/useI18n'
import { useRoleColors } from '@/features/gvg-planner/composables'

import { boardEn } from './locales/en.ts'
import { boardRu } from './locales/ru.ts'

const props = defineProps<RoleBadgeProps>()

const catalog = defineLocaleCatalog({ en: boardEn, ru: boardRu })
const messages = useLocaleMessages(catalog)
const roleColors = useRoleColors()

const label = computed<string>(() => {
  switch (props.role) {
    case CardRole.Tank:
      return messages.value.role.tank
    case CardRole.Dps:
      return messages.value.role.dps
    case CardRole.Hybrid:
      return messages.value.role.hybrid
    case CardRole.Heal:
      return messages.value.role.heal
    case CardRole.Nameless:
      return messages.value.role.nameless
  }
})

const leftPath = computed<WeaponRoleColor | 'none'>(() => props.leftPath ?? 'none')
const rightPath = computed<WeaponRoleColor | 'none'>(() => props.rightPath ?? props.leftPath ?? 'none')

const leftBgClass = computed<string>(() =>
  leftPath.value === 'none' ? 'bg-bg-surface' : roleColors.byColor[leftPath.value].bg,
)
const rightBgClass = computed<string>(() =>
  rightPath.value === 'none' ? 'bg-bg-surface' : roleColors.byColor[rightPath.value].bg,
)

/**
 * True when the badge has at least one weapon-equipped (colored) half.
 * The role-tinted backgrounds are mid-tone and `text-text-inverse` (near-black)
 * reads on them at ≥ 4.5:1, so the colored-path state keeps the existing dark
 * text. The unarmed / no-weapons state renders `bg-bg-surface` (also near-black)
 * under both halves, where dark text collapses to a ~1:1 ratio — flip to
 * `text-text-primary` (warm white) for that branch so the label stays legible
 * without hardcoding a value.
 */
const hasColoredPath = computed<boolean>(
  () => leftPath.value !== 'none' || rightPath.value !== 'none',
)
const labelTextClass = computed<string>(() =>
  hasColoredPath.value ? 'text-text-inverse' : 'text-text-primary',
)

const title = computed<string>(() => {
  const l = props.leftPath ?? messages.value.role.unarmed
  const r = props.rightPath ?? props.leftPath ?? messages.value.role.unarmed
  return messages.value.role.badgeTitle(label.value, l, r)
})
</script>
